/**
 * Wizard-side hook that previews per-option AvailabilityRule decisions.
 *
 * Builds the same request shape used at submit time so the UI can disable
 * unavailable warranty options (with a short explanation) before the user
 * tries to submit. The actual enforcement still happens server-side in
 * `HoldingListCreateView.post`; this hook is purely cosmetic guidance.
 *
 * The query key includes the entire request payload, so any change to
 * host attributes or registration field values triggers a re-evaluation.
 */

import { useQuery } from '@tanstack/react-query'
import {
  availabilityKey,
  evaluateAvailabilityRules,
  type AvailabilityEvaluateOption,
  type AvailabilityResult,
} from '@/admin/hooks/use-availability-rules'
import { useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import type { WarrantyTemplateSummary } from '@/admin/types/products'

interface UseWarrantyAvailabilityArgs {
  templates: WarrantyTemplateSummary[]
  productUuid: string | null
  productTemplateUuid: string | null
  /** Field values keyed by `code_name`. */
  fields: Record<string, unknown>
  /** Per-entity attribute snapshots (host, consumer, …). */
  entities: Record<string, Record<string, unknown>>
  /** Skip evaluation entirely (e.g. when no templates or flag is off). */
  enabled?: boolean
}

export interface WarrantyAvailabilityMap {
  /** Lookup by `availabilityKey(templateUuid, optionUuid)`. */
  byOption: Map<string, AvailabilityResult>
  /** Lookup by `templateUuid` (template-scope rule with no option_id). */
  byTemplate: Map<string, AvailabilityResult>
  /** True while the network request is in flight. */
  isLoading: boolean
}

export function useWarrantyAvailability({
  templates,
  productUuid,
  productTemplateUuid,
  fields,
  entities,
  enabled = true,
}: UseWarrantyAvailabilityArgs): WarrantyAvailabilityMap {
  const rulesEnabled = useFeatureFlag('registration_rules')

  const options: AvailabilityEvaluateOption[] = []
  for (const tmpl of templates) {
    if (tmpl.options.length === 0) {
      options.push({
        template_uuid: tmpl.uuid,
        option_uuid: null,
        product_uuid: productUuid,
        product_template_uuid: productTemplateUuid,
      })
      continue
    }
    for (const opt of tmpl.options) {
      options.push({
        template_uuid: tmpl.uuid,
        option_uuid: opt.uuid,
        product_uuid: productUuid,
        product_template_uuid: productTemplateUuid,
      })
    }
  }

  const body = {
    context: { fields, entities },
    options,
  }

  const query = useQuery({
    queryKey: ['availability-rules', 'evaluate', body],
    queryFn: () => evaluateAvailabilityRules(body),
    enabled: enabled && rulesEnabled && options.length > 0,
    staleTime: 10_000,
  })

  const byOption = new Map<string, AvailabilityResult>()
  const byTemplate = new Map<string, AvailabilityResult>()
  for (const r of query.data?.results ?? []) {
    byOption.set(availabilityKey(r.template_uuid, r.option_uuid), r)
    if (r.option_uuid === null && !r.available) {
      byTemplate.set(r.template_uuid, r)
    }
  }

  return {
    byOption,
    byTemplate,
    isLoading: query.isFetching,
  }
}
